import { useState } from 'react';
import { Card, Table, Badge, Alert, Row, Col, Button, Tabs, Tab, Accordion } from 'react-bootstrap';
import { Link } from 'react-router-dom';

const AugmentationJobDetails = ({ augmentationJob, loading, error, onRetry, retrying }) => {
  const [activeTab, setActiveTab] = useState('overview');
  const getStatusBadge = (status) => {
    const variants = {
      'COMPLETED': 'success',
      'NEEDS_REVIEW_AUGMENTATION': 'warning',
      'FAILED': 'danger',
      'PROCESSING': 'primary',
      'PENDING': 'info'
    };
    
    const variant = variants[status] || 'secondary';
    return <Badge bg={variant}>{status}</Badge>;
  };

  const formatDuration = (milliseconds) => {
    if (!milliseconds) return 'N/A';
    return `${(milliseconds / 1000).toFixed(2)}s`;
  };

  const renderDataComparison = () => {
    if (!augmentationJob) return null;

    // Use the already-parsed objects instead of parsing JSON strings
    const originalData = augmentationJob.inputData || {};
    const augmentedData = augmentationJob.augmentedData || {};

    // Check if we have any data at all
    if (Object.keys(originalData).length === 0 && Object.keys(augmentedData).length === 0) {
      return (
        <Alert variant="info">
          No data comparison available. Both input and augmented data are empty.
        </Alert>
      );
    }

    // Find new fields that were added during augmentation
    const originalKeys = Object.keys(originalData);
    const augmentedKeys = Object.keys(augmentedData);
    const newFields = augmentedKeys.filter(key => !originalKeys.includes(key));
    const modifiedFields = originalKeys.filter(key => 
      augmentedKeys.includes(key) && 
      JSON.stringify(originalData[key]) !== JSON.stringify(augmentedData[key])
    );

    return (
      <div>
        {/* Summary of changes */}
        {(newFields.length > 0 || modifiedFields.length > 0) && (
          <Alert variant="info" className="mb-3">
            <strong>Augmentation Summary:</strong> 
            {newFields.length > 0 && <span className="ms-2">{newFields.length} new fields added</span>}
            {modifiedFields.length > 0 && <span className="ms-2">{modifiedFields.length} fields modified</span>}
          </Alert>
        )}

        <Row>
          <Col md={6}>
            <h6>Original Extracted Data</h6>
            <div className="border rounded p-3 bg-light" style={{ maxHeight: '400px', overflowY: 'auto' }}>
              {Object.entries(originalData).map(([key, value]) => (
                <div key={key} className={`mb-2 ${modifiedFields.includes(key) ? 'bg-warning bg-opacity-25 p-1 rounded' : ''}`}>
                  <strong>{key}:</strong>{' '}
                  <code>{JSON.stringify(value)}</code>
                </div>
              ))}
            </div>
          </Col>
          <Col md={6}>
            <h6>Augmented Data</h6>
            <div className="border rounded p-3 bg-light" style={{ maxHeight: '400px', overflowY: 'auto' }}>
              {Object.entries(augmentedData).map(([key, value]) => (
                <div key={key} className={`mb-2 ${
                  newFields.includes(key) ? 'bg-success bg-opacity-25 p-1 rounded' : 
                  modifiedFields.includes(key) ? 'bg-warning bg-opacity-25 p-1 rounded' : ''
                }`}>
                  <strong>{key}:</strong>{' '}
                  <code>{JSON.stringify(value)}</code>
                  {newFields.includes(key) && <Badge bg="success" className="ms-2 small">NEW</Badge>}
                  {modifiedFields.includes(key) && <Badge bg="warning" className="ms-2 small">MODIFIED</Badge>}
                </div>
              ))}
            </div>
          </Col>
        </Row>

        {/* Show only new fields if there are many */}
        {newFields.length > 0 && (
          <div className="mt-4">
            <h6>New Fields Added by Augmentation</h6>
            <div className="border rounded p-3 bg-success bg-opacity-10">
              {newFields.map(key => (
                <div key={key} className="mb-2">
                  <strong>{key}:</strong>{' '}
                  <code>{JSON.stringify(augmentedData[key])}</code>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderFailedRules = () => {
    if (!augmentationJob || !augmentationJob.ruleExecutionLog) {
      return <Alert variant="info">No rule execution data available.</Alert>;
    }

    // Filter rules that failed or require review from the execution log
    const failedOrReviewRules = augmentationJob.ruleExecutionLog.filter(rule => 
      !rule.success || rule.requiresReview || rule.reviewReason
    );

    if (failedOrReviewRules.length === 0) {
      return <Alert variant="success">All rules executed successfully without issues.</Alert>;
    }

    return (
      <div>
        {failedOrReviewRules.map((rule, index) => (
          <Card key={index} className="mb-3">
            <Card.Header>
              <h6 className="mb-0">
                {rule.ruleName || `Rule ${rule.ruleId}`} 
                {rule.ruleType && <Badge bg="secondary" className="ms-2">{rule.ruleType}</Badge>}
              </h6>
            </Card.Header>
            <Card.Body>
              <Row>
                <Col md={6}>
                  <div className="mb-2">
                    <strong>Execution Order:</strong> {rule.executionOrder}
                  </div>
                  <div className="mb-2">
                    <strong>Status:</strong>{' '}
                    <Badge bg={rule.success ? 'warning' : 'danger'}>
                      {rule.success ? 'Requires Review' : 'Failed'}
                    </Badge>
                  </div>
                  <div className="mb-2">
                    <strong>Processing Time:</strong> {formatDuration(rule.executionTimeMs)}
                  </div>
                </Col>
                <Col md={6}>
                  {rule.reviewReason && (
                    <div className="mb-2">
                      <strong>Review Reason:</strong>{' '}
                      <Badge bg="warning">{rule.reviewReason}</Badge>
                    </div>
                  )}
                  {rule.errorMessage && (
                    <Alert variant="danger" className="mb-2">
                      <strong>Error:</strong> {rule.errorMessage}
                    </Alert>
                  )}
                </Col>
              </Row>
              
              {/* Show input/output for failed rules */}
              {(rule.input || rule.output) && (
                <div className="mt-3">
                  <Row>
                    {rule.input && (
                      <Col md={6}>
                        <h6>Input Data</h6>
                        <div className="border rounded p-2 bg-light" style={{ maxHeight: '200px', overflowY: 'auto' }}>
                          <pre className="mb-0 small">{JSON.stringify(rule.input, null, 2)}</pre>
                        </div>
                      </Col>
                    )}
                    {rule.output && (
                      <Col md={6}>
                        <h6>Output Data</h6>
                        <div className="border rounded p-2 bg-light" style={{ maxHeight: '200px', overflowY: 'auto' }}>
                          <pre className="mb-0 small">{JSON.stringify(rule.output, null, 2)}</pre>
                        </div>
                      </Col>
                    )}
                  </Row>
                </div>
              )}
            </Card.Body>
          </Card>
        ))}
      </div>
    );
  };

  const renderRuleExecutionDetails = () => {
    if (!augmentationJob || !augmentationJob.ruleExecutionLog) {
      return <Alert variant="info">No rule execution details available.</Alert>;
    }

    const executionLog = augmentationJob.ruleExecutionLog;
    if (!Array.isArray(executionLog) || executionLog.length === 0) {
      return <Alert variant="info">No rule execution details available.</Alert>;
    }

    return (
      <div>
        {/* Summary stats */}
        <Row className="mb-4">
          <Col md={3}>
            <div className="text-center">
              <div className="h4 text-primary mb-1">{executionLog.length}</div>
              <div className="text-muted small">Total Rules</div>
            </div>
          </Col>
          <Col md={3}>
            <div className="text-center">
              <div className="h4 text-success mb-1">{executionLog.filter(r => r.success && !r.requiresReview).length}</div>
              <div className="text-muted small">Successful</div>
            </div>
          </Col>
          <Col md={3}>
            <div className="text-center">
              <div className="h4 text-warning mb-1">{executionLog.filter(r => r.requiresReview).length}</div>
              <div className="text-muted small">Need Review</div>
            </div>
          </Col>
          <Col md={3}>
            <div className="text-center">
              <div className="h4 text-danger mb-1">{executionLog.filter(r => !r.success).length}</div>
              <div className="text-muted small">Failed</div>
            </div>
          </Col>
        </Row>

        <Accordion defaultActiveKey="0">
          {executionLog.map((execution, index) => (
            <Accordion.Item key={index} eventKey={index.toString()}>
              <Accordion.Header>
                <div className="d-flex justify-content-between align-items-center w-100 me-3">
                  <div>
                    <strong>{execution.ruleName || `Rule ${execution.ruleId}`}</strong>
                    {execution.ruleType && (
                      <Badge bg="info" className="ms-2 text-uppercase">
                        {execution.ruleType}
                      </Badge>
                    )}
                  </div>
                  <div className="d-flex gap-2">
                    <Badge bg={
                      !execution.success ? 'danger' : 
                      execution.requiresReview ? 'warning' : 
                      'success'
                    }>
                      {!execution.success ? 'Failed' : 
                       execution.requiresReview ? 'Needs Review' : 
                       'Success'}
                    </Badge>
                    <span className="text-muted small">
                      {formatDuration(execution.executionTimeMs)}
                    </span>
                  </div>
                </div>
              </Accordion.Header>
              <Accordion.Body>
                <Row>
                  <Col md={6}>
                    <Table borderless size="sm">
                      <tbody>
                        <tr>
                          <td><strong>Rule ID:</strong></td>
                          <td>{execution.ruleId}</td>
                        </tr>
                        <tr>
                          <td><strong>Execution Order:</strong></td>
                          <td>{execution.executionOrder || index + 1}</td>
                        </tr>
                        <tr>
                          <td><strong>Rule Type:</strong></td>
                          <td>
                            <Badge bg="info" className="text-uppercase">
                              {execution.ruleType || 'N/A'}
                            </Badge>
                          </td>
                        </tr>
                        <tr>
                          <td><strong>Status:</strong></td>
                          <td>
                            <Badge bg={
                              !execution.success ? 'danger' : 
                              execution.requiresReview ? 'warning' : 
                              'success'
                            }>
                              {!execution.success ? 'Failed' : 
                               execution.requiresReview ? 'Needs Review' : 
                               'Success'}
                            </Badge>
                          </td>
                        </tr>
                        <tr>
                          <td><strong>Processing Time:</strong></td>
                          <td>{formatDuration(execution.executionTimeMs)}</td>
                        </tr>
                      </tbody>
                    </Table>
                  </Col>
                  <Col md={6}>
                    {execution.errorMessage && (
                      <Alert variant="danger">
                        <strong>Error Message:</strong><br />
                        {execution.errorMessage}
                      </Alert>
                    )}
                    {execution.requiresReview && (
                      <Alert variant="warning">
                        <strong>Review Required</strong>
                        {execution.reviewReason && (
                          <>
                            <br />
                            <strong>Reason:</strong> {execution.reviewReason}
                          </>
                        )}
                      </Alert>
                    )}
                  </Col>
                </Row>

                {/* Show input/output data for each rule */}
                {(execution.input || execution.output) && (
                  <div className="mt-3">
                    <Row>
                      {execution.input && (
                        <Col md={6}>
                          <h6>Input Data</h6>
                          <div className="border rounded p-2 bg-light" style={{ maxHeight: '300px', overflowY: 'auto' }}>
                            <pre className="mb-0 small">{JSON.stringify(execution.input, null, 2)}</pre>
                          </div>
                        </Col>
                      )}
                      {execution.output && (
                        <Col md={6}>
                          <h6>Output Data</h6>
                          <div className="border rounded p-2 bg-light" style={{ maxHeight: '300px', overflowY: 'auto' }}>
                            <pre className="mb-0 small">{JSON.stringify(execution.output, null, 2)}</pre>
                          </div>
                        </Col>
                      )}
                    </Row>
                  </div>
                )}
              </Accordion.Body>
            </Accordion.Item>
          ))}
        </Accordion>
      </div>
    );
  };

  if (loading) {
    return (
      <Alert variant="info">
        <i className="bi bi-hourglass-split me-2"></i>
        Loading augmentation job details...
      </Alert>
    );
  }

  if (error) {
    return (
      <Alert variant="warning">
        <Alert.Heading>Augmentation Information</Alert.Heading>
        <p>{error}</p>
      </Alert>
    );
  }

  if (!augmentationJob) {
    return (
      <Alert variant="info">
        <Alert.Heading>No Augmentation Job</Alert.Heading>
        <p>No augmentation job has been created for this extraction yet. Augmentation jobs are automatically created when extraction completes successfully.</p>
      </Alert>
    );
  }

  return (
    <div className="mt-3">
      <Tabs 
        activeKey={activeTab}
        onSelect={(k) => setActiveTab(k)}
        className="mb-3"
      >
        <Tab eventKey="overview" title="Overview">
          {/* Job Overview */}
          <Card className="mb-4">
            <Card.Header className="d-flex justify-content-between align-items-center">
              <h6 className="mb-0">Job Overview</h6>
              <div className="d-flex gap-2">
                {augmentationJob.status === 'FAILED' && onRetry && (
                  <Button 
                    variant="outline-primary"
                    size="sm"
                    onClick={onRetry}
                    disabled={retrying}
                  >
                    {retrying ? (
                      <>
                        <i className="bi bi-arrow-clockwise me-1"></i>
                        Retrying...
                      </>
                    ) : (
                      <>
                        <i className="bi bi-arrow-clockwise me-1"></i>
                        Retry
                      </>
                    )}
                  </Button>
                )}
                {augmentationJob.status === 'NEEDS_REVIEW_AUGMENTATION' && (
                  <Button 
                    as={Link}
                    to={`/augmentation-review/${augmentationJob.id}`}
                    variant="warning"
                    size="sm"
                  >
                    <i className="bi bi-exclamation-triangle me-1"></i>
                    Review Required
                  </Button>
                )}
                <Button 
                  as={Link}
                  to={`/augmentation-jobs/${augmentationJob.id}`}
                  variant="outline-primary"
                  size="sm"
                >
                  <i className="bi bi-arrow-right me-1"></i>
                  View Full Details
                </Button>
              </div>
            </Card.Header>
            <Card.Body>
              <Row>
                <Col md={6}>
                  <Table borderless size="sm">
                    <tbody>
                      <tr>
                        <td><strong>Job ID:</strong></td>
                        <td>{augmentationJob.id}</td>
                      </tr>
                      <tr>
                        <td><strong>Status:</strong></td>
                        <td>{getStatusBadge(augmentationJob.status)}</td>
                      </tr>
                      <tr>
                        <td><strong>Created:</strong></td>
                        <td>{new Date(augmentationJob.createdAt).toLocaleString()}</td>
                      </tr>
                      {augmentationJob.completedAt && (
                        <tr>
                          <td><strong>Completed:</strong></td>
                          <td>{new Date(augmentationJob.completedAt).toLocaleString()}</td>
                        </tr>
                      )}
                    </tbody>
                  </Table>
                </Col>
                <Col md={6}>
                  <Table borderless size="sm">
                    <tbody>
                      <tr>
                        <td><strong>Rules Executed:</strong></td>
                        <td>{augmentationJob.rulesExecuted || 0}</td>
                      </tr>
                      <tr>
                        <td><strong>Processing Time:</strong></td>
                        <td>{formatDuration(augmentationJob.totalProcessingTimeMs)}</td>
                      </tr>
                      {augmentationJob.statusReason && (
                        <tr>
                          <td><strong>Status Reason:</strong></td>
                          <td>
                            <Badge bg="secondary" className="me-1">
                              {augmentationJob.statusReason}
                            </Badge>
                          </td>
                        </tr>
                      )}
                      {augmentationJob.errorMessage && (
                        <tr>
                          <td><strong>Error:</strong></td>
                          <td className="text-danger">{augmentationJob.errorMessage}</td>
                        </tr>
                      )}
                    </tbody>
                  </Table>
                </Col>
              </Row>
            </Card.Body>
          </Card>

          {/* Processing Metrics */}
          {augmentationJob.status === 'COMPLETED' && (
            <Card className="mb-4">
              <Card.Header>
                <h6 className="mb-0">Processing Metrics</h6>
              </Card.Header>
              <Card.Body>
                <Row>
                  <Col md={3}>
                    <div className="text-center">
                      <div className="h4 text-primary mb-1">{augmentationJob.rulesExecuted || 0}</div>
                      <div className="text-muted small">Rules Executed</div>
                    </div>
                  </Col>
                  <Col md={3}>
                    <div className="text-center">
                      <div className="h4 text-success mb-1">{formatDuration(augmentationJob.totalProcessingTimeMs)}</div>
                      <div className="text-muted small">Processing Time</div>
                    </div>
                  </Col>
                  <Col md={3}>
                    <div className="text-center">
                      <div className="h4 text-info mb-1">
                        {augmentationJob.createdAt && augmentationJob.completedAt 
                          ? formatDuration(new Date(augmentationJob.completedAt) - new Date(augmentationJob.createdAt))
                          : 'N/A'
                        }
                      </div>
                      <div className="text-muted small">Total Duration</div>
                    </div>
                  </Col>
                  <Col md={3}>
                    <div className="text-center">
                      <div className="h4 text-success mb-1">✓</div>
                      <div className="text-muted small">Status</div>
                    </div>
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          )}
        </Tab>

        <Tab eventKey="data-comparison" title="Data Comparison">
          {/* Input vs Output Data Comparison */}
          {(augmentationJob.inputData || augmentationJob.augmentedData) ? (
            <Card className="mb-4">
              <Card.Header>
                <h6 className="mb-0">Data Transformation</h6>
              </Card.Header>
              <Card.Body>
                {renderDataComparison()}
              </Card.Body>
            </Card>
          ) : (
            <Alert variant="info">No data comparison available.</Alert>
          )}
        </Tab>

        <Tab eventKey="failed-rules" title={`Failed Rules (${augmentationJob?.ruleExecutionLog?.filter(rule => !rule.success || rule.requiresReview || rule.reviewReason)?.length || 0})`}>
          {renderFailedRules()}
        </Tab>

        <Tab eventKey="rule-execution" title="Rule Execution Details">
          {renderRuleExecutionDetails()}
        </Tab>
      </Tabs>
    </div>
  );
};

export default AugmentationJobDetails;